import { authFetch, clearToken } from "./utils/auth";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useState, useEffect, useRef, useCallback } from "react";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import ErrorBoundary from "./components/ErrorBoundary";
import { safeGet, safeSet, safeRemove, safeGetJSON, storageWorks } from "./utils/safeStorage";

// Pages
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import Onboarding from "./pages/Onboarding";
import Session from "./pages/Session";
import CoachDrills from "./pages/CoachDrills";
import CoachDashboard from "./pages/CoachDashboard";
import CoachStudents from "./pages/CoachStudents";
import CoachStudentDetail from "./pages/CoachStudentDetail";
import CoachLibrary from "./pages/CoachLibrary";
import CoachAnalytics from "./pages/CoachAnalytics";
import JoinCoach from "./pages/JoinCoach";
import WelcomeTour from "./pages/WelcomeTour";
import OnCourseSession from "./pages/OnCourseSession";
import Profile from "./pages/Profile";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Admin from "./pages/Admin";

const API = `${window.location.origin}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

// --- Device info helper ---
function getDeviceInfo() {
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS/.test(ua);
  const isChromeiOS = /CriOS/.test(ua);
  const isPWA = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
  let browser = "unknown";
  if (isChromeiOS) browser = "Chrome iOS";
  else if (isSafari && isIOS) browser = "Safari iOS";
  else if (isSafari) browser = "Safari Mac";
  else if (/Chrome/.test(ua)) browser = "Chrome";
  else if (/Firefox|FxiOS/.test(ua)) browser = "Firefox";
  else if (/Edg/.test(ua)) browser = "Edge";
  let deviceType = "desktop";
  if (/Mobi|Android/i.test(ua) || isIOS) deviceType = "mobile";
  else if (/Tablet|iPad/i.test(ua)) deviceType = "tablet";
  return { device_type: deviceType, browser, is_ios: isIOS, is_safari: isSafari, is_pwa: isPWA };
}

// --- Client Error Logger (enriched) ---
function logClientError(error, context = {}) {
  try {
    const device = getDeviceInfo();
    const payload = {
      message: error?.message || String(error),
      stack: (error?.stack || "").slice(0, 2000),
      route: window.location.pathname,
      build_version: window.__GFP_VERSION || "unknown",
      user_agent: navigator.userAgent,
      device_type: device.device_type,
      browser: device.browser,
      is_ios: device.is_ios,
      is_safari: device.is_safari,
      is_pwa: device.is_pwa,
      storage_available: storageWorks,
      timestamp: new Date().toISOString(),
      ...context
    };
    fetch(`${API}/client-errors`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    }).catch(() => {});
  } catch (e) {}
}

// Global error handlers
window.addEventListener("error", (event) => {
  logClientError(event.error || event.message, { type: "window.onerror" });
});
window.addEventListener("unhandledrejection", (event) => {
  logClientError(event.reason, { type: "unhandledrejection" });
});

// Auth Callback Component
const AuthCallback = () => {
  const [status, setStatus] = useState("Checking authentication...");
  const [error, setError] = useState(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");
      const hash = window.location.hash;
      const sessionId = hash ? new URLSearchParams(hash.substring(1)).get("session_id") : null;
      
      if (code) {
        setStatus("Processing Google sign-in...");
        try {
          const redirectUri = `${window.location.origin}/auth/callback`;
          const response = await fetch(`${API}/auth/google/callback`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ code, redirect_uri: redirectUri }),
          });
          
          if (response.ok) {
            const user = await response.json();
            if (user.token) safeSet("gfp_token", user.token);
            setStatus(`Welcome ${user.name || 'Golfer'}!`);
            window.history.replaceState(null, "", "/");
            const targetPath = user.onboarding_complete 
              ? (user.welcome_tour_complete ? "/dashboard" : "/welcome")
              : "/onboarding";
            setTimeout(() => window.location.replace(targetPath), 500);
          } else {
            const errData = await response.json().catch(() => ({ detail: 'Authentication failed' }));
            setError(`Auth failed: ${response.status} - ${errData.detail || 'Unknown error'}`);
          }
        } catch (err) {
          setError(`Network error: ${err.message}`);
        }
      } else if (sessionId) {
        setStatus("Found session, verifying...");
        try {
          const response = await fetch(`${API}/auth/session`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ session_id: sessionId }),
          });
          
          if (response.ok) {
            const user = await response.json();
            if (user.token) safeSet("gfp_token", user.token);
            window.history.replaceState(null, "", "/");
            const targetPath = user.onboarding_complete ? "/dashboard" : "/onboarding";
            setTimeout(() => window.location.replace(targetPath), 500);
          } else {
            const errText = await response.text();
            setError(`Auth failed: ${response.status} - ${errText}`);
          }
        } catch (err) {
          setError(`Network error: ${err.message}`);
        }
      } else {
        setError("No authorization code found. Please try signing in again.");
      }
    };
    
    processAuth();
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0f0d] flex items-center justify-center">
      <div className="text-center max-w-md p-6">
        {error ? (
          <>
            <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <span className="text-red-400 text-xl">!</span>
            </div>
            <p className="text-red-400 font-medium mb-4">{error}</p>
            <button onClick={() => window.location.href = "/"} className="px-4 py-2 bg-emerald-500 text-white rounded-lg">
              Back to Home
            </button>
          </>
        ) : (
          <>
            <div className="w-12 h-12 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-400">{status}</p>
          </>
        )}
      </div>
    </div>
  );
};

// Storage warning banner
function StorageWarning() {
  if (storageWorks) return null;
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-amber-600 text-white text-center py-2 px-4 text-xs" data-testid="storage-warning">
      Limited storage mode — some preferences may not persist. Try disabling private browsing.
    </div>
  );
}

// Protected Route — renders app shell immediately, auth check is non-blocking to UI skeleton
const ProtectedRoute = ({ children, requireSubscription = false }) => {
  const [authState, setAuthState] = useState({ phase: "checking", user: null, error: null });
  const location = useLocation();
  const retryCount = useRef(0);

  const checkAuth = useCallback(async () => {
    const token = safeGet("gfp_token");
    if (!token) {
      setAuthState({ phase: "unauthenticated", user: null, error: null });
      return;
    }
    setAuthState(prev => ({ ...prev, phase: "checking", error: null }));
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      const response = await fetch(`${API}/auth/me`, {
        headers: { "Authorization": `Bearer ${token}` },
        signal: controller.signal
      });
      clearTimeout(timeout);
      
      if (response.ok) {
        const user = await response.json();
        safeSet("gfp_was_authenticated", "1");
        setAuthState({ phase: "ready", user, error: null });
        retryCount.current = 0;
      } else if (response.status === 401) {
        safeRemove("gfp_token");
        const wasAuth = safeGet("gfp_was_authenticated");
        setAuthState({ phase: wasAuth ? "expired" : "unauthenticated", user: null, error: null });
      } else {
        logClientError(new Error(`Auth check returned ${response.status}`), { type: "auth_check" });
        setAuthState({ phase: "error", user: null, error: `Server error (${response.status})` });
      }
    } catch (error) {
      logClientError(error, { type: "auth_check_network" });
      setAuthState({ phase: "error", user: null, error: error.message });
    }
  }, []);

  useEffect(() => {
    if (location.state?.user) {
      setAuthState({ phase: "ready", user: location.state.user, error: null });
      return;
    }
    checkAuth();
  }, [location, checkAuth]);

  // 5-second timeout for checking phase
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    if (authState.phase !== "checking") { setTimedOut(false); return; }
    const timer = setTimeout(() => setTimedOut(true), 5000);
    return () => clearTimeout(timer);
  }, [authState.phase]);

  // Saved session for recovery
  const hasSavedSession = safeGetJSON("gfp_active_session");
  const validSavedSession = hasSavedSession?.missionId && Date.now() - (hasSavedSession.savedAt || 0) < 86400000 ? hasSavedSession : null;

  // Recovery UI helper
  const RecoveryScreen = ({ title, subtitle, showRetry = true, showLogin = true }) => (
    <div className="min-h-screen bg-[#0a0f0d] flex items-center justify-center p-6" data-testid="recovery-screen">
      <div className="text-center max-w-sm">
        <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
          <span className="text-amber-400 text-2xl">!</span>
        </div>
        <h2 className="text-white font-bold text-lg mb-2" data-testid="recovery-title">{title}</h2>
        <p className="text-gray-400 text-sm mb-6">{subtitle}</p>
        <div className="space-y-3">
          {showRetry && (
            <button onClick={() => { retryCount.current++; setTimedOut(false); checkAuth(); }}
              className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-medium transition-colors"
              data-testid="retry-btn">Reload App</button>
          )}
          {showLogin && (
            <button onClick={() => window.location.href = "/"}
              className="w-full py-3 bg-white/10 hover:bg-white/15 text-white rounded-xl text-sm transition-colors"
              data-testid="go-login-btn">Go to Login</button>
          )}
          {validSavedSession && (
            <button onClick={() => window.location.href = `/session/${validSavedSession.missionId}`}
              className="w-full py-3 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-xl text-sm transition-colors"
              data-testid="resume-from-recovery-btn">Resume Session</button>
          )}
          <button onClick={() => {
            if ("caches" in window) caches.keys().then(keys => keys.forEach(k => caches.delete(k)));
            if ("serviceWorker" in navigator) navigator.serviceWorker.getRegistrations().then(regs => regs.forEach(r => r.unregister()));
            safeRemove("gfp_was_authenticated");
            window.location.reload(true);
          }}
            className="w-full py-2.5 text-gray-500 hover:text-gray-300 text-xs transition-colors"
            data-testid="clear-cache-recovery-btn">Clear Cache & Reload</button>
        </div>
      </div>
    </div>
  );

  // CHECKING: show app shell skeleton (not blank, not just spinner)
  if (authState.phase === "checking" && !timedOut) {
    return (
      <div className="min-h-screen bg-[#0a0f0d]" data-testid="auth-loading-shell">
        {/* Minimal shell skeleton */}
        <div className="px-6 py-3 border-b border-white/10">
          <div className="max-w-4xl mx-auto flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-emerald-500/20 animate-pulse" />
            <div className="w-28 h-5 rounded bg-white/10 animate-pulse" />
          </div>
        </div>
        <div className="px-6 py-8 max-w-4xl mx-auto space-y-4">
          <div className="w-40 h-4 rounded bg-white/5 animate-pulse" />
          <div className="w-64 h-8 rounded bg-white/10 animate-pulse" />
          <div className="h-40 rounded-xl bg-white/5 animate-pulse" />
          <div className="h-14 rounded-xl bg-emerald-500/10 animate-pulse" />
        </div>
      </div>
    );
  }

  // TIMED OUT or ERROR
  if (timedOut || authState.phase === "error") {
    return <RecoveryScreen
      title={timedOut ? "Something went wrong loading the app." : "Connection issue"}
      subtitle={timedOut ? "The app could not initialize. Try one of these:" : "Could not reach the server. Check your connection."}
    />;
  }

  // EXPIRED session
  if (authState.phase === "expired") {
    return (
      <div className="min-h-screen bg-[#0a0f0d] flex items-center justify-center p-6" data-testid="auth-recovery-screen">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-white font-bold text-lg mb-2" data-testid="auth-recovery-title">Session expired</h2>
          <p className="text-gray-400 text-sm mb-6">Please sign in again to continue.</p>
          <div className="space-y-3">
            <button onClick={() => window.location.href = "/"}
              className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-medium transition-colors"
              data-testid="auth-recovery-signin-btn">Sign In</button>
            <button onClick={() => window.location.reload()}
              className="w-full py-3 bg-white/10 hover:bg-white/15 text-white rounded-xl text-sm transition-colors"
              data-testid="auth-recovery-reload-btn">Reload</button>
            {validSavedSession && (
              <button onClick={() => {
                safeSet("gfp_resume_after_login", validSavedSession.missionId);
                window.location.href = "/";
              }}
                className="w-full py-3 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-xl text-sm transition-colors"
                data-testid="auth-recovery-resume-btn">Resume Session After Sign-In</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // NOT AUTHENTICATED (first visit)
  if (authState.phase === "unauthenticated" || !authState.user) {
    return <Navigate to="/" replace />;
  }

  // SUBSCRIPTION CHECK (beta bypasses)
  if (requireSubscription && !authState.user.is_subscriber && !authState.user.is_beta_user) {
    return (
      <div className="min-h-screen bg-[#0a0f0d] flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <h2 className="text-white text-2xl font-bold mb-2">Members Only</h2>
          <p className="text-gray-400 mb-6">This feature requires a GolfFlowPro membership.</p>
          <a href="/#pricing" className="bg-emerald-500 text-white px-6 py-3 rounded-xl font-medium inline-block mb-3">
            Become a Member
          </a>
          <br />
          <button onClick={() => window.history.back()} className="text-gray-500 hover:text-white transition-colors mt-2">Go Back</button>
        </div>
      </div>
    );
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();
  const [isAuthCallback, setIsAuthCallback] = useState(false);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash?.includes("session_id=")) {
      setIsAuthCallback(true);
    }
  }, [location]);

  if (window.location.hash?.includes("session_id=") || isAuthCallback) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/drills" element={<Navigate to="/dashboard" replace />} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/welcome" element={<ProtectedRoute><WelcomeTour /></ProtectedRoute>} />
      <Route path="/on-course/:missionId" element={<ProtectedRoute><OnCourseSession /></ProtectedRoute>} />
      <Route path="/session/:missionId" element={<ProtectedRoute><Session /></ProtectedRoute>} />
      <Route path="/coach-drills" element={<ProtectedRoute><CoachDrills /></ProtectedRoute>} />
      <Route path="/coach" element={<ProtectedRoute><CoachDashboard /></ProtectedRoute>} />
      <Route path="/coach/students" element={<ProtectedRoute><CoachStudents /></ProtectedRoute>} />
      <Route path="/coach/students/:studentId" element={<ProtectedRoute><CoachStudentDetail /></ProtectedRoute>} />
      <Route path="/coach/library" element={<ProtectedRoute><CoachLibrary /></ProtectedRoute>} />
      <Route path="/coach/analytics" element={<ProtectedRoute><CoachAnalytics /></ProtectedRoute>} />
      <Route path="/join/:inviteCode" element={<JoinCoach />} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/admin" element={<Admin />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

// Top-level recovery screen (used by boot timeout)
function AppRecovery() {
  return (
    <div className="min-h-screen bg-[#0a0f0d] flex items-center justify-center p-6" data-testid="boot-recovery">
      <div className="text-center max-w-sm">
        <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
          <span className="text-red-400 text-2xl">!</span>
        </div>
        <h2 className="text-white font-bold text-lg mb-2" data-testid="boot-recovery-title">
          Something went wrong loading the app.
        </h2>
        <p className="text-gray-400 text-sm mb-6">
          GolfFlowPro could not start. This can happen on iOS after an update or with a stale cache.
        </p>
        <div className="space-y-3">
          <button onClick={() => window.location.reload()}
            className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-medium transition-colors"
            data-testid="boot-reload-btn">Reload App</button>
          <button onClick={() => {
            if ("caches" in window) caches.keys().then(keys => keys.forEach(k => caches.delete(k)));
            if ("serviceWorker" in navigator) navigator.serviceWorker.getRegistrations().then(regs => regs.forEach(r => r.unregister()));
            safeRemove("gfp_was_authenticated");
            setTimeout(() => window.location.reload(true), 200);
          }}
            className="w-full py-3 bg-white/10 hover:bg-white/15 text-white rounded-xl text-sm transition-colors"
            data-testid="boot-clear-cache-btn">Clear Cache & Reload</button>
          <button onClick={() => window.location.href = "/"}
            className="w-full py-3 bg-white/10 hover:bg-white/15 text-white rounded-xl text-sm transition-colors"
            data-testid="boot-go-login-btn">Go to Login</button>
        </div>
        <p className="text-gray-600 text-xs mt-4">
          Build: {window.__GFP_VERSION || "unknown"} | {getDeviceInfo().browser}
        </p>
      </div>
    </div>
  );
}

// Boot state machine: INIT → CHECKING → READY | RECOVERY
function App() {
  const [bootPhase, setBootPhase] = useState("init");
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);

  useEffect(() => {
    setBootPhase("checking");

    // 5-second hard recovery timeout
    const recoveryTimer = setTimeout(() => {
      setBootPhase((prev) => {
        if (prev !== "ready") {
          logClientError(new Error("Boot timeout after 5s"), { type: "boot_timeout" });
          return "recovery";
        }
        return prev;
      });
    }, 5000);

    // Version check: fast, non-blocking — always resolves to 'ready'
    const bootPromise = fetch(`${API}/version`, { signal: AbortSignal.timeout?.(4000) })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`Version ${r.status}`))))
      .then((d) => {
        window.__GFP_VERSION = d.version;
        // Version mismatch detection: if cached version differs, force reload once
        const cachedVersion = safeGet("gfp_build_version");
        if (cachedVersion && cachedVersion !== d.version) {
          safeSet("gfp_build_version", d.version);
          // Only force reload if SW exists, to clear stale cache
          if ("serviceWorker" in navigator) {
            navigator.serviceWorker.getRegistrations().then((regs) =>
              regs.forEach((r) => r.unregister())
            ).then(() => {
              if ("caches" in window) caches.keys().then((keys) => keys.forEach((k) => caches.delete(k)));
              window.location.reload(true);
            }).catch(() => {});
            return; // Don't set ready — page will reload
          }
        }
        safeSet("gfp_build_version", d.version);
      })
      .catch(() => {
        window.__GFP_VERSION = "unknown";
      })
      .finally(() => {
        clearTimeout(recoveryTimer);
        setBootPhase((prev) => (prev !== "recovery" ? "ready" : prev));
      });

    // Service worker lifecycle: iOS-safe
    if ("serviceWorker" in navigator) {
      try {
        navigator.serviceWorker.addEventListener("message", (event) => {
          if (event.data?.type === "SW_UPDATED") {
            setShowUpdateBanner(true);
          }
        });

        // Check for waiting worker or updates (with timeout for iOS)
        const swReady = navigator.serviceWorker.ready;
        const swTimeout = new Promise((_, reject) => setTimeout(() => reject(new Error("SW ready timeout")), 3000));
        Promise.race([swReady, swTimeout])
          .then((reg) => {
            if (reg.waiting) setShowUpdateBanner(true);
            reg.addEventListener("updatefound", () => {
              const newWorker = reg.installing;
              if (newWorker) {
                newWorker.addEventListener("statechange", () => {
                  if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
                    setShowUpdateBanner(true);
                  }
                });
              }
            });
            // Trigger an update check
            reg.update().catch(() => {});
          })
          .catch(() => {}); // SW ready timeout — safe to ignore on iOS
      } catch (e) {}
    }

    // Visibility change handler: re-check for SW updates when app is reopened (iOS/Android PWA)
    const handleVisibility = () => {
      if (document.visibilityState === "visible" && "serviceWorker" in navigator) {
        try {
          navigator.serviceWorker.ready.then((reg) => reg.update()).catch(() => {});
        } catch (e) {}
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      clearTimeout(recoveryTimer);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  // Boot: loading
  if (bootPhase === "init" || bootPhase === "checking") {
    return (
      <div className="min-h-screen bg-[#0a0f0d] flex items-center justify-center" data-testid="boot-loading">
        <div className="text-center max-w-sm p-6">
          <div className="w-12 h-12 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400 text-sm">Loading GolfFlowPro...</p>
        </div>
      </div>
    );
  }

  // Boot: recovery (5s timeout hit)
  if (bootPhase === "recovery") {
    return <AppRecovery />;
  }

  // Boot: ready
  return (
    <ErrorBoundary>
      <BrowserRouter>
        {/* Storage warning for Safari private mode */}
        <StorageWarning />
        {showUpdateBanner && (
          <div className="fixed top-0 left-0 right-0 z-[60] bg-emerald-600 text-white text-center py-2.5 px-4 text-sm flex items-center justify-center gap-3"
            data-testid="update-banner">
            <span>New version available</span>
            <button onClick={() => {
              if ("serviceWorker" in navigator) {
                navigator.serviceWorker.ready.then(reg => { if (reg.waiting) reg.waiting.postMessage({ type: "SKIP_WAITING" }); }).catch(() => {});
              }
              window.location.reload();
            }} className="underline font-medium" data-testid="update-refresh-btn">Refresh now</button>
            <button onClick={() => setShowUpdateBanner(false)} className="ml-2 opacity-60 hover:opacity-100">&times;</button>
          </div>
        )}
        <AppRouter />
        <Toaster position="top-right" />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
